/**
 * This function sanitizes an object by removing keys with values that match specified removable types.
 * By default, it removes keys with values of: null, undefined, empty array ([]), and empty object ({}).
 * You can customize which values to remove by providing an array of types in the second argument.
 *
 * @template T - The expected return type after cleaning.
 * @param obj - The object to be sanitized.
 * @param remove - An optional array of removable value types. Supported types: 'null', 'undefined', 'emptyArray', 'emptyObject'.
 *                 Defaults to ['null', 'undefined', 'emptyArray', 'emptyObject'].
 * @returns A cleaned object without the specified unwanted values, or `undefined` if all properties are removed.
 */

export function removeNullishFromObj<T>(
  obj: Record<string, unknown> | undefined | null,
  remove: RemovableType[] = ['null', 'undefined', 'emptyArray', 'emptyObject']
): T | undefined {
  if (!obj) return undefined;

  const cleaned = Object.fromEntries(
    Object.entries(obj).filter(([, value]) => {
      if (remove.includes('null') && value === null) return false;
      if (remove.includes('undefined') && value === undefined) return false;
      if (remove.includes('emptyString') && value === '') return false;
      if (remove.includes('false') && value === false) return false;
      if (remove.includes('true') && value === true) return false;
      if (
        remove.includes('emptyArray') &&
        Array.isArray(value) &&
        value.length === 0
      )
        return false;
      if (
        remove.includes('emptyObject') &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        value !== null &&
        Object.keys(value).length === 0
      )
        return false;

      return true;
    })
  );

  return Object.keys(cleaned).length === 0 ? undefined : (cleaned as T);
}

type RemovableType =
  | 'null'
  | 'undefined'
  | 'emptyArray'
  | 'emptyObject'
  | 'emptyString'
  | 'true'
  | 'false';
